import { useState, useEffect, useRef, useCallback } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Pencil, Trash2, Plus, Share2, Save, Linkedin, Instagram, Facebook, ExternalLink, Copy, Check, Eye, Phone, Mail, MessageCircle, CreditCard } from 'lucide-react';
import HelpTip from '@/components/HelpTip';

const SOCIAL_NETWORKS = [
  { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin },
  { key: 'instagram', label: 'Instagram', Icon: Instagram },
  { key: 'facebook', label: 'Facebook', Icon: Facebook },
  { key: 'tiktok', label: 'TikTok', Icon: null },
  { key: 'threads', label: 'Threads', Icon: null },
];

const LINK_OPTIONS = [
  { enabledKey: 'crm_site_enabled', urlKey: 'catalog_url' },
  { enabledKey: 'external_site_enabled', urlKey: 'external_url' },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const VCARD_PUBLIC_BASE = process.env.REACT_APP_VCARD_PUBLIC_URL || 'https://app.dealider.com';

export default function VirtualCardPage() {
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [newCert, setNewCert] = useState(null);
  const [certErrors, setCertErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const photoRef = useRef(null);
  const coverRef = useRef(null);
  const savedCardRef = useRef(null);

  const fetchCard = useCallback(async () => {
    try {
      const { data } = await api.get('/v1/vcard/me');
      setCard(data);
      savedCardRef.current = JSON.stringify(data);
      setIsDirty(false);
    } catch (e) {
      toast.error('Error al cargar tarjeta');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCard(); }, [fetchCard]);

  const handleChange = (field, value) => {
    setCard(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (fieldErrors[field]) {
      setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  // CAMBIO 1: Validación de switches activos sin URL
  const validateSwitches = () => {
    const errors = {};
    for (const { key } of SOCIAL_NETWORKS) {
      if (card[`${key}_enabled`] && !(card[`${key}_url`] || '').trim()) {
        errors[`${key}_url`] = 'Este campo es obligatorio si el campo está activado.';
      }
    }
    for (const { enabledKey, urlKey } of LINK_OPTIONS) {
      if (card[enabledKey] && !(card[urlKey] || '').trim()) {
        errors[urlKey] = 'Este campo es obligatorio si el campo está activado.';
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateSwitches()) {
      toast.error('Completa todos los campos obligatorios antes de guardar.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/v1/vcard/me', card);
      setCard(data);
      savedCardRef.current = JSON.stringify(data);
      setIsDirty(false);
      setFieldErrors({});
      toast.success('Tarjeta guardada correctamente');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/v1/vcard/me/${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCard(prev => ({ ...prev, [`${type}_url`]: data[`${type}_url`] }));
      toast.success(type === 'photo' ? 'Foto actualizada' : 'Portada actualizada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al subir imagen');
    }
  };

  const startAddCert = () => {
    setNewCert({ title: '', year_start: '' });
    setCertErrors({});
  };

  // CAMBIO 3: Validación ambos campos al confirmar cert
  const confirmAddCert = async () => {
    const errs = {};
    if (!(newCert?.title || '').trim()) errs.title = 'Ingresa el título o certificación.';
    if (!(newCert?.year_start || '').trim()) errs.year_start = 'Ingresa el año.';
    setCertErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      const { data } = await api.post('/v1/vcard/me/items', newCert);
      setCard(prev => ({ ...prev, certifications: [...(prev.certifications || []), data] }));
      setNewCert(null);
      setCertErrors({});
      toast.success('Certificación agregada');
    } catch (e) {
      toast.error('Error al agregar certificación');
    }
  };

  const cancelAddCert = () => { setNewCert(null); setCertErrors({}); };

  const updateCertLocal = (id, field, value) => {
    setCard(prev => ({
      ...prev,
      certifications: prev.certifications.map(c => c.id === id ? { ...c, [field]: value } : c),
    }));
    setIsDirty(true);
  };

  const saveCertification = async (cert) => {
    try {
      await api.put(`/v1/vcard/me/items/${cert.id}`, cert);
    } catch (e) { /* silent */ }
  };

  const deleteCertification = async (id) => {
    try {
      await api.delete(`/v1/vcard/me/items/${id}`);
      setCard(prev => ({ ...prev, certifications: prev.certifications.filter(c => c.id !== id) }));
      toast.success('Certificación eliminada');
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const openShare = async () => {
    if (isDirty) {
      toast.warning('Tienes cambios sin guardar. Por favor guarda los cambios antes de compartir tu tarjeta.');
      return;
    }
    setShareOpen(true);
    try {
      const resp = await api.get('/v1/vcard/me/qr', { responseType: 'blob' });
      setQrUrl(URL.createObjectURL(resp.data));
    } catch { setQrUrl(''); }
  };

  const publicUrl = card?.slug ? `${VCARD_PUBLIC_BASE}/vcard/${card.slug}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(`¡Hola! Te comparto mi tarjeta de presentación virtual.\nPuedes ver mis propiedades y contactarme desde aquí:\n${publicUrl}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground" data-testid="vcard-loading">Cargando...</div>;
  if (!card) return <div className="text-center py-20 text-muted-foreground">No se pudo cargar la tarjeta</div>;

  const photoSrc = card.photo_url ? (card.photo_url.startsWith('http') ? card.photo_url : `${API_BASE}${card.photo_url}`) : '';
  const coverSrc = card.cover_url ? (card.cover_url.startsWith('http') ? card.cover_url : `${API_BASE}${card.cover_url}`) : '';

  return (
    <div className="space-y-4 sm:space-y-6 max-w-7xl mx-auto px-2 sm:px-0" data-testid="virtual-card-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2" data-testid="vcard-title">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0" />
            Mi Tarjeta Virtual
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Personaliza tu tarjeta de presentación digital</p>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <Button variant="outline" onClick={openShare} data-testid="vcard-share-btn" className="gap-2 text-xs sm:text-sm">
            <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Compartir</span> Tarjeta
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="vcard-save-btn" className="gap-2 bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold text-xs sm:text-sm">
            <Save className="w-4 h-4" /> {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-8 space-y-4 sm:space-y-6">
          {/* Identity */}
          <section className="p-4 sm:p-6 rounded-xl bg-card border border-border" data-testid="vcard-identity-section">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 mb-6">
              <div className="relative group shrink-0">
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-muted border-2 border-border">
                  {photoSrc ? <img src={photoSrc} alt="Perfil" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-3xl font-bold">{card.full_name?.charAt(0) || '?'}</div>}
                </div>
                <button onClick={() => photoRef.current?.click()} className="absolute -bottom-2 -right-2 p-1.5 bg-primary rounded-lg text-primary-foreground shadow-lg hover:scale-110 transition-transform" data-testid="vcard-photo-edit">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={e => handlePhotoUpload(e, 'photo')} />
              </div>
              <div className="flex-1 w-full space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Nombre Completo</label>
                  <Input value={card.full_name || ''} onChange={e => handleChange('full_name', e.target.value)} data-testid="vcard-full-name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Posición / Cargo</label>
                  <Input value={card.position_title || ''} onChange={e => handleChange('position_title', e.target.value)} data-testid="vcard-position" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Empresa</label>
                  <Input value={card.company_name || ''} onChange={e => handleChange('company_name', e.target.value)} data-testid="vcard-company" />
                </div>
              </div>
            </div>

            {/* Cover */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Imagen de Portada</label>
              <div className="relative h-28 sm:h-32 rounded-lg overflow-hidden bg-muted border border-border cursor-pointer group" onClick={() => coverRef.current?.click()}>
                {coverSrc ? <img src={coverSrc} alt="Cover" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">Click para subir portada</div>}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Pencil className="w-5 h-5 text-white" />
                </div>
              </div>
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={e => handlePhotoUpload(e, 'cover')} />
            </div>

            {/* Bio */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Experiencia Profesional</label>
              <textarea
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none"
                rows={4}
                maxLength={1000}
                value={card.professional_bio || ''}
                onChange={e => handleChange('professional_bio', e.target.value)}
                data-testid="vcard-bio"
              />
              <p className="text-xs text-muted-foreground text-right mt-1">{(card.professional_bio || '').length}/1000</p>
            </div>
          </section>

          {/* Contact */}
          <section className="p-4 sm:p-6 rounded-xl bg-card border border-border" data-testid="vcard-contact-section">
            <h3 className="text-sm font-bold uppercase tracking-widest mb-4">Contacto Directo</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Teléfono Móvil</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input className="pl-10" value={card.phone_mobile || ''} onChange={e => handleChange('phone_mobile', e.target.value)} data-testid="vcard-phone" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Correo Electrónico</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input className="pl-10" value={card.email || ''} onChange={e => handleChange('email', e.target.value)} data-testid="vcard-email" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">WhatsApp</label>
                <div className="relative">
                  <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input className="pl-10" value={card.whatsapp || ''} onChange={e => handleChange('whatsapp', e.target.value)} placeholder="+52 000 000 0000" data-testid="vcard-whatsapp" />
                </div>
              </div>
            </div>
          </section>

          {/* xmail: no borrar (Grok) */}
          <section className="p-4 sm:p-6 rounded-xl bg-card border border-border" data-testid="vcard-xmail-section">
            <h3 className="text-sm font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
              Correo del sistema (X-MAIL)
              <HelpTip title="Correo del sistema" content="Este alias es para escribirle a tus clientes desde LeadCEM. No reemplaza el correo de contacto de esta tarjeta. El administrador elige si se ve como tu@lcem.tudominio o tu.lcem@tudominio." />
            </h3>
            <p className="text-[12px] text-muted-foreground mb-3">El correo de contacto de arriba sigue siendo el tuyo. X-MAIL es aparte, para el trabajo con leads.</p>
            <Button type="button" variant="outline" size="sm" onClick={() => { window.location.href = '/x-mail'; }} data-testid="vcard-open-xmail" className="gap-2">
              <Mail className="w-4 h-4" /> Abrir X-MAIL
            </Button>
          </section>

          {/* Certifications */}
          <section className="p-4 sm:p-6 rounded-xl bg-card border border-border" data-testid="vcard-certs-section">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-bold uppercase tracking-widest">Trayectoria y Certificaciones</h3>
              <Button variant="outline" size="sm" onClick={startAddCert} disabled={!!newCert} data-testid="vcard-add-cert" className="gap-2">
                <Plus className="w-4 h-4" /> Añadir Ítem
              </Button>
            </div>
            {(card.certifications?.length > 0 || newCert) && (
              <div className="flex items-center gap-4 px-3 sm:px-4 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <span className="flex-1">Título/Certificación</span>
                <span className="w-24 text-center">Año(s)</span>
                <span className="w-9"></span>
              </div>
            )}
            <div className="space-y-3">
              {(card.certifications || []).map(cert => (
                <div key={cert.id} className="flex items-center gap-3 sm:gap-4 p-3 rounded-xl bg-background border border-border hover:border-primary/30 transition-all" data-testid={`vcard-cert-${cert.id}`}>
                  <div className="flex-1 min-w-0">
                    <Input
                      value={cert.title || ''}
                      onChange={e => updateCertLocal(cert.id, 'title', e.target.value)}
                      onBlur={() => saveCertification(cert)}
                      placeholder="Nombre de certificación..."
                    />
                  </div>
                  <div className="w-24 shrink-0">
                    <Input
                      value={cert.year_start || ''}
                      onChange={e => updateCertLocal(cert.id, 'year_start', e.target.value)}
                      onBlur={() => saveCertification(cert)}
                      placeholder="Año"
                    />
                  </div>
                  <button onClick={() => deleteCertification(cert.id)} className="p-2 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors shrink-0" data-testid={`vcard-delete-cert-${cert.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {/* Inline new row — CAMBIO 2: same Input styles + CAMBIO 3: inline errors */}
              {newCert && (
                <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 animate-in fade-in duration-200 space-y-2" data-testid="vcard-new-cert-row">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <Input
                        autoFocus
                        value={newCert.title}
                        onChange={e => { setNewCert(p => ({ ...p, title: e.target.value })); if (certErrors.title) setCertErrors(p => ({ ...p, title: '' })); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmAddCert(); if (e.key === 'Escape') cancelAddCert(); }}
                        placeholder="Nombre de certificación..."
                        data-testid="vcard-new-cert-title"
                      />
                    </div>
                    <div className="w-24 shrink-0">
                      <Input
                        value={newCert.year_start}
                        onChange={e => { setNewCert(p => ({ ...p, year_start: e.target.value })); if (certErrors.year_start) setCertErrors(p => ({ ...p, year_start: '' })); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmAddCert(); if (e.key === 'Escape') cancelAddCert(); }}
                        placeholder="Año"
                        data-testid="vcard-new-cert-year"
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={confirmAddCert} className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors" data-testid="vcard-confirm-cert">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={cancelAddCert} className="p-2 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" data-testid="vcard-cancel-cert">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {/* Inline errors */}
                  {(certErrors.title || certErrors.year_start) && (
                    <div className="flex items-center gap-3 sm:gap-4 px-1">
                      <div className="flex-1 min-w-0">
                        {certErrors.title && <p className="text-xs text-red-400" data-testid="vcard-cert-error-title">{certErrors.title}</p>}
                      </div>
                      <div className="w-24 shrink-0">
                        {certErrors.year_start && <p className="text-xs text-red-400" data-testid="vcard-cert-error-year">{certErrors.year_start}</p>}
                      </div>
                      <div className="w-[4.5rem] shrink-0"></div>
                    </div>
                  )}
                </div>
              )}
              {(!card.certifications || card.certifications.length === 0) && !newCert && (
                <p className="text-center text-sm text-muted-foreground py-6">Sin certificaciones aún. Haz click en &quot;Añadir Ítem&quot; para agregar.</p>
              )}
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-4 space-y-4 sm:space-y-6">
          {/* Social Networks */}
          <section className="p-4 sm:p-6 rounded-xl bg-card border border-border" data-testid="vcard-social-section">
            <h3 className="text-sm font-bold uppercase tracking-widest mb-6">Redes Sociales</h3>
            <div className="space-y-5">
              {SOCIAL_NETWORKS.map(({ key, label }) => (
                <div key={key}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold">{label}</span>
                    <Switch
                      checked={!!card[`${key}_enabled`]}
                      onCheckedChange={v => handleChange(`${key}_enabled`, v)}
                      data-testid={`vcard-toggle-${key}`}
                    />
                  </div>
                  <Input
                    value={card[`${key}_url`] || ''}
                    onChange={e => handleChange(`${key}_url`, e.target.value)}
                    placeholder="URL de perfil"
                    className="text-xs"
                    disabled={!card[`${key}_enabled`]}
                    data-testid={`vcard-url-${key}`}
                  />
                  {fieldErrors[`${key}_url`] && (
                    <p className="text-xs text-red-400 mt-1" data-testid={`vcard-error-${key}`}>{fieldErrors[`${key}_url`]}</p>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Link Options */}
          <section className="p-4 sm:p-6 rounded-xl bg-card border border-border" data-testid="vcard-links-section">
            <h3 className="text-sm font-bold uppercase tracking-widest mb-6">Opciones de Enlace</h3>
            <div className="space-y-5">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div>
                    <p className="text-sm font-semibold">Mi inventario de propiedades</p>
                    <p className="text-[10px] text-muted-foreground">Enlazar a tu catálogo {APP_NAME}</p>
                  </div>
                  <Switch checked={!!card.crm_site_enabled} onCheckedChange={v => handleChange('crm_site_enabled', v)} data-testid="vcard-toggle-crm" />
                </div>
                {card.crm_site_enabled && (
                  <>
                    <Input
                      value={card.catalog_url || ''}
                      onChange={e => handleChange('catalog_url', e.target.value)}
                      placeholder="ej: dealider.com/marcus"
                      className="text-xs"
                      data-testid="vcard-catalog-url"
                    />
                    {fieldErrors.catalog_url && (
                      <p className="text-xs text-red-400" data-testid="vcard-error-catalog">{fieldErrors.catalog_url}</p>
                    )}
                  </>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div>
                    <p className="text-sm font-semibold">Inventario de propiedades de la inmobiliaria</p>
                    <p className="text-[10px] text-muted-foreground">Redirigir a URL personalizada</p>
                  </div>
                  <Switch checked={!!card.external_site_enabled} onCheckedChange={v => handleChange('external_site_enabled', v)} data-testid="vcard-toggle-external" />
                </div>
                {card.external_site_enabled && (
                  <>
                    <Input
                      value={card.external_url || ''}
                      onChange={e => handleChange('external_url', e.target.value)}
                      placeholder="https://..."
                      className="text-xs"
                      data-testid="vcard-external-url"
                    />
                    {fieldErrors.external_url && (
                      <p className="text-xs text-red-400" data-testid="vcard-error-external">{fieldErrors.external_url}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Share Modal */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="w-[90vw] max-w-md overflow-hidden" data-testid="vcard-share-modal">
          <DialogHeader>
            <DialogTitle>Compartir Tarjeta Virtual</DialogTitle>
            <DialogDescription>Comparte tu tarjeta digital con tus contactos</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 overflow-y-auto max-h-[70vh] px-1">
            <div className="flex flex-col items-center">
              {qrUrl ? (
                <div className="bg-white rounded-lg p-2 shadow-inner mb-3">
                  <img src={qrUrl} alt="QR Code" className="w-48 h-48" data-testid="vcard-qr-image" />
                </div>
              ) : (
                <div className="w-48 h-48 bg-muted rounded-lg flex items-center justify-center mb-3">
                  <span className="text-muted-foreground text-sm">Generando QR...</span>
                </div>
              )}
              <p className="text-sm text-muted-foreground text-center">Escanea para visualizar la tarjeta</p>
            </div>

            <Button onClick={shareWhatsApp} className="w-full py-5 bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 border border-[#25D366]/30 font-bold gap-3" variant="outline" data-testid="vcard-share-whatsapp">
              <MessageCircle className="w-5 h-5" /> Compartir en WhatsApp
            </Button>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase">Enlace Directo</label>
              <div className="flex items-center bg-background border border-border rounded-lg p-1 min-w-0">
                <span className="min-w-0 flex-1 px-3 text-sm truncate" data-testid="vcard-public-url">{publicUrl}</span>
                <Button size="icon" variant="ghost" onClick={copyLink} className="shrink-0" data-testid="vcard-copy-link">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex justify-center">
              <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 text-sm font-bold flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-primary/10 transition-colors" data-testid="vcard-view-link">
                <Eye className="w-4 h-4" /> Ver mi tarjeta de presentación virtual
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
